// Pain-risk helpers (Phase B of the pain map track). Derive per-exercise
// risk from the catalog row's `primary[]` / `secondary[]` arrays against
// the user's painMap. No medical advice — the helpers return how much
// an exercise mechanically loads a given muscle, leaving the actual
// load decision to the lifter (memory: project_bodymap_base + the
// 2026-05-17 "no consigli medici" call from Antenore).
//
// Derivation rule: a muscle that appears in `primary[]` (after
// CATALOG_TO_CANONICAL bridging) gets `red` for the pain layer (heavy
// direct load on this muscle). `secondary[]` gets `yellow` (stabilizer
// or synergist). Everything else returns `undefined` (the exercise
// doesn't load this muscle in a way the catalog tracks). Joint-stress
// cases that primary/secondary doesn't capture (squat → lower-back,
// bench → shoulder joint, etc.) live in a small hand-curated overrides
// map below; the LLM-driven richer matrix is a future ship if the
// overrides become unwieldy.

import { mapCatalogToCanonical, type MuscleSlug, type PainSeverity } from "./bodymap.js";
import type { Exercise } from "./schemas/exercise.js";
import { PAIN_RANK, painSides, strongerPain } from "./painSides.js";
import type { PainMap } from "./painSides.js";
import { BODY_SIDES, type BodySide, type PerSide, type Side } from "./side.js";

// Joint-stress overrides — exercises that load a muscle/region the
// catalog's primary/secondary doesn't capture. Each entry adds the
// listed muscles at the given severity if the catalog row didn't
// already place them at a stronger severity (red beats yellow beats
// nothing). Keep this list short; if it grows past ~50 entries we
// graduate to the LLM-classified matrix.
//
// ⚠️ Eight of these keys match no slug in the bundled taxonomy, and never
// have — `barbell-good-morning`, `barbell-bent-over-row`, `bent-over-row`,
// `barbell-bench-press`, `incline-dumbbell-bench-press`,
// `barbell-overhead-press`, `barbell-deadlift`, `weighted-chin-up`. The
// taxonomy calls those lifts `good-morning`, `barbell-row`, `bench-press`,
// `incline-dumbbell-press`, `overhead-press`, `deadlift`; it has no
// weighted chin-up at all. The lookup below is a raw key match with no
// alias resolution, so for any row seeded from that taxonomy these eight
// never fire.
//
// That is not eight missing warnings, and re-keying them would not add
// one: each of those taxonomy rows already carries the muscle in its own
// primary/secondary list, which yields a severity at least as strong. This
// table exists for rows whose muscle lists DON'T describe the stabilizer
// load — and these ones do. Adding the taxonomy-correct keys would change
// no behaviour while making the table look like it covers lifts whose
// coverage actually comes from elsewhere, so the keys are left as they are.
//
// They are left rather than deleted for a reason: `Exercise.slug` is
// client-supplied and is NOT validated against the taxonomy, so a
// hand-created or auto-imported row can carry any of these names. For such
// a row the entry is not inert — it may be the only source of the warning,
// since an auto_imported row can arrive with sparse muscle lists. Inert
// against the seeded catalog, live against an imported one; deleting them
// would only be safe against the half we can see.
//
// `overrides vs the catalog vocabulary` in painRisk.test.ts holds the
// equivalence for the seeded half: if a taxonomy edit ever drops one of
// those muscles, it goes red and names the lift, instead of the warning
// quietly disappearing.
const PAIN_OVERRIDES: Readonly<
  Record<string, ReadonlyArray<{ slug: MuscleSlug; severity: PainSeverity }>>
> = {
  // Heavy compounds load the spinal erectors as anti-flexion stabilizers
  // even when the catalog row's primary is quad / glute / chain-driven.
  "back-squat": [{ slug: "lower-back", severity: "yellow" }],
  "front-squat": [{ slug: "lower-back", severity: "yellow" }],
  "barbell-good-morning": [{ slug: "lower-back", severity: "red" }],
  "barbell-bent-over-row": [{ slug: "lower-back", severity: "yellow" }],
  "bent-over-row": [{ slug: "lower-back", severity: "yellow" }],
  "pendlay-row": [{ slug: "lower-back", severity: "yellow" }],
  "barbell-shrug": [{ slug: "lower-back", severity: "yellow" }],

  // Overhead presses load the front-delts even when chest is the
  // catalog primary, plus the wrist isn't in our slug set so flag
  // grip-loaded variants via forearm.
  "barbell-bench-press": [{ slug: "front-deltoids", severity: "yellow" }],
  "dumbbell-bench-press": [{ slug: "front-deltoids", severity: "yellow" }],
  "incline-bench-press": [{ slug: "front-deltoids", severity: "red" }],
  "incline-dumbbell-bench-press": [{ slug: "front-deltoids", severity: "red" }],
  "barbell-overhead-press": [{ slug: "front-deltoids", severity: "red" }],

  // Deadlift family — lower-back is in primary (post-1.69.4 sweep) but
  // upper-back also takes anti-flexion load on heavy pulls.
  "barbell-deadlift": [{ slug: "upper-back", severity: "yellow" }],
  "clean-deadlift": [{ slug: "upper-back", severity: "yellow" }],
  "pause-deadlift": [{ slug: "upper-back", severity: "yellow" }],
  "deficit-deadlift": [{ slug: "upper-back", severity: "yellow" }],

  // Pull-up / chin-up family loads the biceps tendons as a junction
  // even though biceps is secondary on the catalog row.
  "pull-up": [{ slug: "biceps", severity: "yellow" }],
  "chin-up": [{ slug: "biceps", severity: "yellow" }],
  "weighted-pull-up": [{ slug: "biceps", severity: "yellow" }],
  "weighted-chin-up": [{ slug: "biceps", severity: "yellow" }],
};

// Quick membership: does this canonical muscle slug appear in the
// exercise's catalog-slug array (with bridge translation)?
const canonicalSetFor = (
  catalogSlugs: readonly string[] | undefined,
): Set<MuscleSlug> => {
  const out = new Set<MuscleSlug>();
  for (const cat of catalogSlugs ?? []) {
    for (const c of mapCatalogToCanonical(cat)) out.add(c);
  }
  return out;
};

// Per-exercise per-muscle load. Returns:
//   'red'    — primary on this exercise (heavy direct load)
//   'yellow' — secondary (stabilizer / synergist) OR a joint-stress override
//   undefined — exercise doesn't load this muscle in a way the catalog
//               tracks. Treat as safe at the consumer.
export function exerciseLoadOnMuscle(
  ex: Exercise,
  slug: MuscleSlug,
): PainSeverity | undefined {
  const primaryC = canonicalSetFor(ex.primary);
  if (primaryC.has(slug)) return "red";
  const secondaryC = canonicalSetFor(ex.secondary);
  let result: PainSeverity | undefined;
  if (secondaryC.has(slug)) result = "yellow";
  const overrides = PAIN_OVERRIDES[ex.slug];
  if (overrides) {
    for (const o of overrides) {
      if (o.slug !== slug) continue;
      result = result ? strongerPain(result, o.severity) : o.severity;
    }
  }
  return result;
}

// Per-(user pain, exercise load) cell severity. Green pain is a user
// override saying "this muscle is fine" — neutralises any load on it.
// Otherwise the cell takes the worse of the two so red user pain on a
// yellow-loaded muscle still surfaces as red.
const combineCell = (
  painSev: PainSeverity,
  loadSev: PainSeverity,
): PainSeverity | undefined => {
  if (painSev === "green") return undefined;
  return strongerPain(painSev, loadSev);
};

// Overall risk of an exercise for a user with the given painMap. Returns
// the worst per-muscle cell across all muscles where the user has marked
// pain AND the exercise loads. `undefined` = the exercise doesn't load
// any of the user's painful muscles. Caller decides how to render
// (Catalog dot colour, EntryTip banner severity, etc).
export type PainRiskSummary = {
  severity: PainSeverity;
  // Painful muscles this exercise touches, with the cell severity so a
  // consumer can list them with per-muscle severity if desired. `side` is
  // "both" when both sides hurt, else the one side that does (PG-14): a
  // consumer names it, so the warning never says "shoulder" for "right
  // shoulder".
  painfulMuscles: ReadonlyArray<{
    slug: MuscleSlug;
    severity: PainSeverity;
    side: Side;
  }>;
};

// One muscle's cell. Pain on both sides → the worse cell, side "both". Pain
// on ONE side (Antenore, 2026-09-24): the flag names that side, and on a
// unilateral exercise red drops to yellow: it can be done from the healthy
// side, which is caution, not avoid. A bilateral lift still loads the
// painful side, so its severity is unchanged.
function muscleCell(
  ex: Exercise,
  sides: PerSide<PainSeverity>,
  load: PainSeverity,
): { severity: PainSeverity; side: Side } | undefined {
  const hit = BODY_SIDES.flatMap((side) => {
    const sev = sides[side];
    const cell = sev ? combineCell(sev, load) : undefined;
    return cell ? [{ side, cell }] : [];
  });
  const [first, second] = hit;
  if (!first) return undefined;
  if (second) {
    return { severity: strongerPain(first.cell, second.cell), side: "both" };
  }
  const severity =
    ex.unilateral === true && first.cell === "red" ? "yellow" : first.cell;
  return { severity, side: first.side };
}

export function exercisePainSeverity(
  ex: Exercise,
  painMap: PainMap | undefined,
): PainRiskSummary | undefined {
  if (!painMap) return undefined;
  const cells: Array<{ slug: MuscleSlug; severity: PainSeverity; side: Side }> =
    [];
  let worst: PainSeverity | undefined;
  for (const [slug, entry] of Object.entries(painMap)) {
    const load = exerciseLoadOnMuscle(ex, slug as MuscleSlug);
    if (!load) continue;
    const cell = muscleCell(ex, painSides(entry), load);
    if (!cell) continue;
    cells.push({ slug: slug as MuscleSlug, ...cell });
    worst = worst ? strongerPain(worst, cell.severity) : cell.severity;
  }
  if (!worst) return undefined;
  // Sort painful muscles by severity descending so the consumer can show
  // the strongest signals first.
  cells.sort((a, b) => PAIN_RANK[b.severity] - PAIN_RANK[a.severity]);
  return { severity: worst, painfulMuscles: cells };
}

// The one side a risk sits on, for a label that names it (PG-14): every
// cell at the headline severity is on the same body side. Mixed sides, or
// pain on both, name no side.
export function painRiskSide(risk: PainRiskSummary): BodySide | undefined {
  const top = risk.painfulMuscles.filter((m) => m.severity === risk.severity);
  const side = top[0]?.side;
  if (!side || side === "both") return undefined;
  return top.every((m) => m.side === side) ? side : undefined;
}
